import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatService } from '../api/chat/chatService';
import toast from 'react-hot-toast';

export const useChannelList = () => {
  return useQuery({
    queryKey: ['chat', 'channel-list'],
    queryFn: () => chatService.getChannelList(),
    refetchInterval: 5000, // Short polling every 5 seconds for channel updates
  });
};

export const useConversation = (channelId: string | undefined) => {
  return useQuery({
    queryKey: ['chat', 'conversation', channelId],
    queryFn: () => chatService.getConversation(channelId!),
    enabled: !!channelId,
    refetchInterval: 3000, // Short polling every 3 seconds for new messages
  });
};

const getAuthUserIdForOptimistic = () => {
  try {
    const rawSession = localStorage.getItem('manproSessionProfile');
    const rawProto = localStorage.getItem('prototypeUserProfile');
    const raw = rawSession || rawProto;
    if (raw) return JSON.parse(raw)?.id;
  } catch {
    // ignore
  }
  return 'owner'; // fallback
};

export const useSendMessage = (currentUserId?: string | number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: chatService.sendMessage,
    onMutate: async (newMsg) => {
      // Optimistic Update
      await queryClient.cancelQueries({ queryKey: ['chat', 'conversation', newMsg.channel_id] });
      const previousMessages = queryClient.getQueryData(['chat', 'conversation', newMsg.channel_id]);
      const resolvedUserId = currentUserId || getAuthUserIdForOptimistic();

      queryClient.setQueryData(['chat', 'conversation', newMsg.channel_id], (old: any) => {
        if (!old) return old;
          return [
            ...old,
            {
              id: `temp-${Date.now()}`,
              channel_id: newMsg.channel_id,
              user_id: resolvedUserId,
              message: newMsg.message,
              reply_to_id: newMsg.reply_to_id,
              created_at: new Date().toISOString(),
              user: {
                first_name: 'Anda',
                last_name: '',
              },
            },
          ];
      });

      return { previousMessages };
    },
    onError: (err, newMsg, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(
          ['chat', 'conversation', newMsg.channel_id],
          context.previousMessages
        );
      }
      toast.error('Gagal mengirim pesan.');
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'conversation', variables.channel_id] });
      queryClient.invalidateQueries({ queryKey: ['chat', 'channel-list'] });
    },
  });
};

export const useCreateChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: chatService.createChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat', 'channel-list'] });
    },
  });
};

export const useToggleReaction = () => {
  return useMutation({
    mutationFn: chatService.toggleReaction,
  });
};

export const useDeleteMessage = () => {
  return useMutation({
    mutationFn: chatService.deleteMessage,
  });
};
